<template>
  <div class="flex flex-col space-y-2.5">
    <div class="flex space-x-2.5">
      <div class="flex flex-col">
        <div class="flex space-x-2.5 pa-2" style="max-height: 100px">
          <v-btn
            @click="getGraphData"
            :loading="fetchingGraphData"
            color="primary"
            >Refresh Chart</v-btn
          >
          <div>
            <v-select
              outlined
              dense
              hide-details
              label="Interval"
              :value="graphParams.interval"
              :items="['weekly', 'monthly', 'yearly']"
              @change="(val) => setGraphParam('interval', val)"
            ></v-select>
          </div>
          <div>
            <v-text-field
              type="date"
              outlined
              dense
              hide-details
              label="date"
              :value="graphParams.date"
              @change="(val) => setGraphParam('date', val)"
            ></v-text-field>
          </div>
        </div>
        <v-divider></v-divider>
        <div class="py-2" style="position: relative; height: 100%">
          <sales-chart
            ref="salesChartRef"
            style="height: 100%; position: absolute; width: 100%"
            :data="salesChartData"
          />
        </div>
      </div>
      <div class="pa-2 flex flex-col space-y-2.5" style="max-width: 400px">
        <div class="flex space-x-2.5">
          <dashboard-config color="primary" />
          <v-btn color="primary" @click="getSummaryData">Refresh Summary</v-btn>
        </div>
        <div class="flex flex-col space-y-2.5">
          <div>
            <v-card outlined class="ma-0">
              <v-card-title class="primary--text">Sales</v-card-title>
              <v-divider></v-divider>
              <v-card-text class="text-right">{{
                formatAccounting(summaryData.sales)
              }}</v-card-text>
            </v-card>
          </div>
          <div>
            <v-card outlined class="ma-0">
              <v-card-title class="primary--text">Purchases</v-card-title>
              <v-divider></v-divider>
              <v-card-text class="text-right">{{
                formatAccounting(summaryData.purchases)
              }}</v-card-text>
            </v-card>
          </div>
          <div>
            <v-card outlined class="ma-0">
              <v-card-title class="primary--text">Expenses</v-card-title>
              <v-divider></v-divider>
              <v-card-text class="text-right">{{
                formatAccounting(summaryData.expenses)
              }}</v-card-text>
            </v-card>
          </div>
          <div>
            <v-card outlined class="ma-0">
              <v-card-title class="primary--text">Collectibles</v-card-title>
              <v-divider></v-divider>
              <v-card-text class="text-right">{{
                formatAccounting(summaryData.collectibles)
              }}</v-card-text>
            </v-card>
          </div>
          <div>
            <v-card outlined class="ma-0">
              <v-card-title class="primary--text">Gross Profit</v-card-title>
              <v-divider></v-divider>
              <v-card-text class="text-right">{{
                formatAccounting(summaryData.grossProfit)
              }}</v-card-text>
            </v-card>
          </div>
          <div>
            <v-card outlined class="ma-0">
              <v-card-title class="primary--text">Net Profit</v-card-title>
              <v-divider></v-divider>
              <v-card-text class="text-right">{{
                formatAccounting(summaryData.netProfit)
              }}</v-card-text>
            </v-card>
          </div>
        </div>
      </div>
    </div>
    <div class="pa-2 flex flex-col space-y-2.5">
      <div style="width: 100%">
        <printouts />
      </div>
      <div class="">
        <v-card style="width: 100%" class="pa-2" outlined>
          <v-toolbar dark flat color="primary">Most Sold Items</v-toolbar>
          <div class="flex space-x-2.5 pt-2 items-center">
            <div>
              <v-text-field
                type="date"
                dense
                hide-details
                :value="mostSoldItemsDateRange[0]"
                @change="dateStartChange"
                outlined
                label="Date Start"
              ></v-text-field>
            </div>
            <div>
              <v-text-field
                type="date"
                dense
                hide-details
                :value="mostSoldItemsDateRange[1]"
                @change="dateEndChange"
                outlined
                label="Date End"
              ></v-text-field>
            </div>
            <div>
              <v-text-field
                label="No of Items."
                dense
                hide-details
                outlined
                type="number"
                v-model="mostSoldItemsLimit"
              ></v-text-field>
            </div>
            <!-- 🔥 NEW — STOCK MULTI-SELECT DROPDOWN -->
            <div style="width: 200px">
              <v-autocomplete
                v-model="selectedStocks"
                :items="stockOptions"
                :item-text="
                  (stock) => `${stock.productName} (${stock.stockCode})`
                "
                item-value="id"
                :search-input.sync="stockSearchInput"
                multiple
                chips
                dense
                hide-selected
                clearable
                hide-details
                outlined
                label="Stocks"
                @update:search-input="getStocksDropdown"
              />
            </div>
            <v-btn color="primary" @click="getMostSoldItems()">Refresh</v-btn>
          </div>
          <div style="position: relative; height: 400px">
            <most-sold-items-chart
              ref="mostSoldItemsChartRef"
              :data="mostSoldItemsChartData"
              style="width: 100%; height: 100%; position: absolute"
            />
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import appAxios from '../../extra-functions/axios.extra'
import {
  firstDayOfMonth,
  formatAccounting,
  getCurrentDate,
  useWatchEffect,
} from '../../lib/utils'
import DashboardConfig from './component/DashboardConfig.vue'
import Printouts from './components/dashboard/Printouts.vue'
import searchDelayExtra from '../../extra-functions/searchDelay.extra'

const [watchEffect, clearWatchEffect] = useWatchEffect()
const stockInputDelay = searchDelayExtra(500)

export default {
  name: 'DashboardV2',
  components: {
    salesChart: () => import('./component/SalesChartV2'),
    mostSoldItemsChart: () => import('./component/MostSoldItemsChart.vue'),
    DashboardConfig,
    Printouts,
  },
  data() {
    return {
      fetchingMostSoldItems: false,
      graphDate: getCurrentDate(),
      fetchingGraphData: false,
      fetchingSummary: false,
      mostSoldItemsLimit: 5,
      mostSoldItemsDateRange: [firstDayOfMonth(), null],
      salesChartData: {
        labels: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
        sales: [0, 0, 0, 0, 0, 0, 0],
        purchases: [0, 0, 0, 0, 0, 0, 0],
        loans: [0, 0, 0, 0, 0, 0, 0],
        expenses: [0, 0, 0, 0, 0, 0, 0],
        losses: [0, 0, 0, 0, 0, 0, 0],
        collections: [0, 0, 0, 0, 0, 0, 0],
      },
      mostSoldItemsChartData: {
        labels: [],
        backgroundColor: [],
        data: [],
        datasetLabel: '',
      },
      graphParams: {
        interval: 'weekly',
        date: getCurrentDate(),
      },
      summaryData: {
        sales: 0,
        purchases: 0,
        losses: 0,
        expenses: 0,
        collectibles: 0,
        transportCost: 0,
        grossProfit: 0,
        netProfit: 0,
      },
      selectedStocks: [],
      stockOptions: [],
      stockSearchInput: null,
    }
  },
  computed: {
    ...mapState({
      axios: (state) => appAxios(state.auth.token),
      storeId: (state) => state.settings.storeId,
    }),
  },
  created() {
    this.getSummaryData()
    this.getMostSoldItems()
  },
  mounted() {
    this.getGraphData()
  },
  destroyed() {
    clearWatchEffect()
  },
  watch: {
    salesChartData(val) {
      if (!this.$refs?.salesChartRef) return
      this.$nextTick(() => {
        this.$refs.salesChartRef.update()
      })
    },
    mostSoldItemsChartData(val) {
      if (!this.$refs.mostSoldItemsChartRef) return
      this.$nextTick(() => {
        this.$refs.mostSoldItemsChartRef.update()
      })
    },
  },
  methods: {
    getGraphData() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          this.fetchingGraphData = true
          const { data } = await this.axios.get('/stores/graph-data', {
            params: { ...this.graphParams },
            signal: controller.signal,
          })
          this.salesChartData = {
            labels: data.labels,
            sales: data.sales,
            purchases: data.purchases,
            losses: data.losses,
            expenses: data.expenses,
            collections: data.consumer_payments,
          }
        } catch (e) {
          console.error(e)
          this.$alertSwalError('oops something went wrong')
        } finally {
          this.fetchingGraphData = false
        }
      }, 'getGraphData')
    },
    getSummaryData() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          this.fetchingSummary = true
          const { data } = await this.axios.get('/stores/summary', {
            signal: controller.signal,
          })
          this.summaryData = {
            ...this.summaryData,
            sales: data.sales,
            purchases: data.purchases,
            losses: data.losses,
            expenses: data.expenses,
            transportCost: data.transport_cost,
            collectibles: data.collectibles,
            grossProfit: data.grossProfit,
            netProfit: data.netProfit,
          }
        } catch (e) {
          console.error(e)
          this.$alertSwalError('oops something went wrong')
        } finally {
          this.fetchingSummary = false
        }
      }, 'getSummaryData')
    },
    getMostSoldItems() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          this.fetchingMostSoldItems = true
          const { data } = await this.axios.get(
            '/v2/dashboard/stock-performance-data',
            {
              params: {
                stocks: this.selectedStocks,
                dateStart: this.mostSoldItemsDateRange[0],
                dateEnd: this.mostSoldItemsDateRange[1],
                limit: this.mostSoldItemsLimit,
              },
            },
          )
          this.mostSoldItemsChartData = {
            labels: data.labels,
            data: data.quantities,
            datasetLabel: data.dateRange,
            backgroundColor: data.colors,
          }
        } catch (e) {
          console.error(e)
          this.$alertSwalError('oops something went wrong')
        } finally {
          this.fetchingMostSoldItems = false
        }
      }, 'getMostSoldItems')
    },
    getStocksDropdown() {
      stockInputDelay(async () => {
        try {
          const { data } = await this.axios.get('/v2/stocks/dropdown', {
            params: { search: this.stockSearchInput },
          })
          this.stockOptions = data.data ?? []
          console.log(this.stockOptions)
        } catch (e) {
          console.error(e)
          this.$alertSwalError('oops something went wrong')
        }
      })
    },
    setGraphParam(key, value) {
      this.graphParams = { ...this.graphParams, [key]: value }
    },
    dateStartChange(date) {
      this.mostSoldItemsDateRange[0] = date
      this.mostSoldItemsDateRange = [...this.mostSoldItemsDateRange]
    },
    dateEndChange(date) {
      this.mostSoldItemsDateRange[1] = date
      this.mostSoldItemsDateRange = [...this.mostSoldItemsDateRange]
    },
    formatAccounting,
  },
}
</script>
